<div class="mt-5 mb-16 w-full px-6 py-4 md:px-16">
  <div class="flex flex-col items-stretch gap-10">
    <div class="border-b border-border-secondary pb-6">
      <h1
        class="mt-5 mb-2 bg-linear-to-b from-gradient-start from-0% via-gradient-middle2 via-50% to-gradient-end to-100% bg-clip-text text-5xl leading-tight text-transparent lg:text-8xl"
      >
        {{ 'pages.checkout.title' | translate }}
      </h1>
      @if (isOrderingAsReseller && selectedOrganizationName()) {
        <span>{{
          'pages.checkout.orderFor'
            | translate: { name: selectedOrganizationName() }
        }}</span>
      }
    </div>
    @if (orderLines()) {
      <div class="flex flex-col-reverse gap-8 md:flex-row">
        <div class="flex flex-grow flex-col gap-4 md:w-1/2 lg:w-3/5">
          <div
            class="flex items-end gap-4 border-b border-border-secondary pb-4"
          >
            @if (organization(); as org) {
              <div>
                <dhp-organization-contact-info
                  [organization]="org"
                ></dhp-organization-contact-info>
              </div>
            }
            <div class="ml-auto flex h-full w-1/2 flex-col justify-start gap-4">
              <!-- If theres an active subscription and there is more than one valid date, show the subscription start date options -->
              @if (activeSubscriptionEndDate() && validDates.length > 1) {
                <div>
                  <h5 class="text-2xl">
                    {{
                      'pages.checkout.overview.subscription.startPeriod'
                        | translate
                    }}
                  </h5>
                </div>
                <div>
                  <select
                    class="input w-full"
                    [ngModel]="selectedSubscriptionStartDateOption"
                    (ngModelChange)="setSubscriptionDates($event)"
                    type="enum"
                  >
                    <option [value]="subscriptionStartDateOption.Now">
                      {{
                        'pages.checkout.overview.subscription.startNow'
                          | translate
                      }}
                    </option>
                    <option
                      [value]="
                        subscriptionStartDateOption.StartNewSubscriptionPeriod
                      "
                    >
                      {{
                        'pages.checkout.overview.subscription.startNewSubscriptionPeriod'
                          | translate
                      }}
                    </option>
                  </select>
                </div>
                <div>
                  <span class="block font-semibold">
                    {{
                      'pages.checkout.overview.subscription.duration'
                        | translate
                    }}
                  </span>
                  <span class="block">
                    {{
                      'pages.checkout.overview.subscription.startDate'
                        | translate
                          : {
                              date: subscriptionPeriodStartDate | localizedDate,
                            }
                    }}
                  </span>
                  <span class="block">
                    {{
                      'pages.checkout.overview.subscription.renewDate'
                        | translate
                          : { date: subscriptionPeriodEndDate | localizedDate }
                    }}
                  </span>
                </div>
              }
              <input
                class="input mt-auto w-full"
                [placeholder]="
                  'components.productCard.customerCostReference' | translate
                "
                [ngModel]="billingCycleReference()"
                (ngModelChange)="billingCycleReference.set($event)"
                autocomplete="off"
              />
            </div>
          </div>

          @if (hasAutoIncludedUpgrades()) {
            <div
              class="flex flex-col gap-3 rounded-lg bg-secondary-background p-6"
            >
              <div class="font-medium">
                {{
                  'pages.checkout.info.autoIncludedUpgradesTitle' | translate
                }}
              </div>
              <div class="text-sm text-text-secondary">
                {{
                  'pages.checkout.info.autoIncludedUpgradesDescription'
                    | translate
                }}
              </div>

              @for (
                upgradeLine of requiredUpgradeOrderLines()
                  | orderBy: ['product.name'];
                track upgradeLine.sourceSubscriptionId
              ) {
                <div
                  class="flex items-center justify-between gap-4 rounded border border-border-secondary bg-white px-4 py-3"
                >
                  <div class="flex flex-col">
                    <span class="font-medium">{{
                      upgradeLine.product.name
                    }}</span>
                    <span class="text-sm text-text-secondary">
                      {{
                        getAutoIncludedUpgradeDescriptionKey(upgradeLine)
                          | translate
                            : getAutoIncludedUpgradeDescriptionParams(
                                upgradeLine
                              )
                      }}
                    </span>
                  </div>
                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-xs font-medium"
                    [ngClass]="getAutoIncludedUpgradeBadgeClasses(upgradeLine)"
                  >
                    {{
                      getAutoIncludedUpgradeBadgeKey(upgradeLine) | translate
                    }}
                  </span>
                </div>
              }
            </div>
          }

          @for (line of orderedOrderLines(); track line.product.id) {
            <dhp-product-card
              [seats]="line.seats"
              [customerCostReference]="line.customerCostReference"
              [minSelected]="getMinSelected(line.product)"
              [product]="line.product"
              [row]="true"
              (seatsChange)="onOrderLineSeatsChange(line.product.id, $event)"
              (customerCostReferenceChange)="
                onOrderLineCustomerCostReferenceChange(line.product.id, $event)
              "
            ></dhp-product-card>
          }

          @for (ci of contentIntegrators() | orderBy: ['name']; track ci) {
            <div
              class="flex max-h-[100px] items-center gap-8 rounded p-8 outline outline-[1.5px] outline-border-secondary transition-all"
            >
              <h4 class="text-2xl font-medium">
                {{ ci.name }}
              </h4>
              <button
                class="btn-primary ml-auto btn"
                (click)="addContentIntegrator(ci)"
              >
                <img
                  class="size-4 brightness-0 invert filter"
                  ngSrc="assets/icons/plus_icon.svg"
                  alt=""
                  height="16"
                  width="16"
                />
              </button>
            </div>
          }
        </div>
        <div class="w-full md:w-1/2 lg:w-2/5">
          @if (checkoutValidationKeys().length > 0) {
            <div
              class="mb-4 flex flex-col gap-2 rounded-lg bg-error-light p-4 text-error-text"
            >
              @for (
                validationKey of checkoutValidationKeys();
                track validationKey
              ) {
                <div>{{ validationKey | translate }}</div>
              }
            </div>
          }
          <dhp-product-checkout-list
            [hasPendingScheduledAdjustments]="
              hasScheduledOnlyUpgradeOrderLines()
            "
            [isCheckoutDisabled]="isCheckoutDisabled()"
            [orderLines]="summaryOrderLines()"
            [organizationId]="selectedOrganizationId()"
            [isProrated]="
              selectedSubscriptionStartDateOption ===
              subscriptionStartDateOption.Now
            "
            [subscriptionStartDate]="subscriptionPeriodStartDate"
            [subscriptionEndDate]="subscriptionPeriodEndDate"
            [submitLabelKey]="
              requiresNextPeriodOrder()
                ? 'pages.checkout.overview.submitNextPeriod'
                : 'pages.checkout.overview.submit'
            "
            (confirm)="openConfirmation()"
          ></dhp-product-checkout-list>
        </div>
      </div>
    }
  </div>
</div>
<!-- Confirmation Dialog -->
<dialog
  class="min-w-1/3 rounded-lg backdrop:bg-black/50 backdrop:backdrop-blur-sm"
  #confirmDialog
>
  <div class="flex flex-col gap-4 rounded-xl p-6">
    @if (isError()) {
      <div class="rounded-xl bg-system-error-red/20 p-4 text-system-error-red">
        <span>{{ 'pages.checkout.confirmation.error' | translate }}</span>
      </div>
    }
    <div class="flex flex-col gap-2">
      <div>{{ 'pages.checkout.confirmation.title' | translate }}</div>
      <div>
        {{
          (requiresNextPeriodOrder()
            ? 'pages.checkout.confirmation.nextPeriodOrder'
            : 'pages.checkout.confirmation.paymentObligation'
          ) | translate
        }}
      </div>
    </div>
    <div class="">
      <label class="flex items-center">
        <input
          class="checkbox"
          (change)="toggleTermsAndConditions()"
          type="checkbox"
        />
        <span
          class="ml-1"
          [innerHTML]="
            'pages.checkout.confirmation.acceptTermsConditions'
              | translate: { termsRoute: termsRoute }
          "
        ></span>
      </label>
    </div>
    <div class="flex justify-end gap-4">
      <button class="btn-secondary btn" (click)="confirmDialog.close()">
        {{ 'pages.checkout.confirmation.cancel' | translate }}
      </button>
      <button
        class="btn-primary btn"
        [disabled]="!hasAcceptedTermsConditions()"
        (click)="checkout()"
      >
        @if (!isCreatingSubscriptions()) {
          <span>{{ 'pages.checkout.confirmation.confirm' | translate }}</span>
        } @else {
          <div
            class="size-4 animate-spin rounded-full border-4 border-white border-t-transparent"
          ></div>
        }
      </button>
    </div>
  </div>
</dialog>
